<script type="ts">
  import { onMount } from "svelte";

  let copyright = `Copyright Curbza  &copy; ${new Date().getFullYear()}. All rights reserved.`;
  const companyAddress = `Curbza <br>
          250 Baker Drive, Unit 236<br>
          Dartmouth, NS<br>
          B2W 6L4<br>`;

  export let socialLinks = [
    {
      name: 'Facebook',
      icon: 'facebook',
      iconPath: '/assets/images/logos/social-logo-facebook.svg',
      link: 'https://www.facebook.com/curbza/',
    },
    {
      name: 'LinkedIn',
      icon: 'linkedin',
      iconPath: '/assets/images/logos/social-logo-linkedin.svg',
      link: 'https://www.linkedin.com/company/curbza-inc',
    },
    {
      name: 'Twitter',
      icon: 'twitter',
      iconPath: '/assets/images/logos/social-logo-twitter.svg',
      link: 'https://twitter.com/curbza',
    },
    {
      name: 'Instagram',
      icon: 'instagram',
      iconPath: '/assets/images/logos/social-logo-instagram.svg',
      link: 'https://www.instagram.com/curbza/',
    },
    {
      name: 'YouTube',
      icon: 'youtube',
      iconPath: '/assets/images/logos/social-logo-youtube.svg',
      link: 'https://www.youtube.com/channel/UChWcGEWQJHl4gZa3XwfSDQw',
    },
  ];

  const POSTS_API_URI = 'https://blog.curbza.com/wp-json/wp/v2/';
  const MEDIA_API_URI = `${POSTS_API_URI}media/`;

  interface IPost {
    readonly id: number;
    date: string;
    date_gmt: string;
    guid: string;
    link: string;
    modified: string;
    modified_gmt: string;
    slug: string;
    status: 'publish' | 'future' | 'draft' | 'pending' | 'private' | 'trash' | 'auto-draft' | 'inherit';
    type: 'post' | 'page' | 'attachment' | 'wp_block' | 'feedback' | 'jp_pay_order' | 'jp_pay_product';
    password?: string;
    permalink_template?: string;
    title: any;
    content: any;
    author: number;
    excerpt: any;
    featured_media: number;
    comment_status?: string;
    ping_status?: string;
    format?: string;
    meta?: any;
    sticky: boolean;
    template?: string;
    categories: string[] | number[];
    tags: string[] | number[];
  }

  export class Post implements IPost {
    readonly id: number;
    date: string;
    date_gmt: string;
    guid: string;
    link: string;
    modified: string;
    modified_gmt: string;
    slug: string;
    status: 'publish' | 'future' | 'draft' | 'pending' | 'private' | 'trash' | 'auto-draft' | 'inherit';
    type: 'post' | 'page' | 'attachment' | 'wp_block' | 'feedback' | 'jp_pay_order' | 'jp_pay_product';
    password?: string;
    permalink_template?: string;
    title: any;
    content: any;
    author: number;
    excerpt: any;
    featured_media: number;
    comment_status?: string;
    ping_status?: string;
    format?: string;
    meta?: any;
    sticky: boolean;
    template?: string;
    categories: string[] | number[];
    tags: string[] | number[];

    get Title(): string {
      return this.title.rendered ? this.title.rendered : '';
    }

    get FeaturedImage(): string {
      return `${MEDIA_API_URI}${this.featured_media}`;
    }

    get Content(): string {
      return this.content.rendered ? this.content.rendered : '';
    }

    get Excerpt(): string {
      return this.excerpt.rendered ? this.excerpt.rendered : '';
    }

    constructor(params: IPost = null) {
      this.id = params && params.id ? params.id : 0;
      this.date = params && params.date ? params.date : '';
      this.date_gmt = params && params.date_gmt ? params.date_gmt : '';
      this.guid = params && params.guid ? params.guid : '';
      this.link = params && params.link ? params.link : '';
      this.modified = params && params.modified ? params.modified : '';
      this.modified_gmt = params && params.modified_gmt ? params.modified_gmt : '';
      this.slug = params && params.slug ? params.slug : '';
      this.status = params && params.status ? params.status : 'draft';
      this.type = params && params.type ? params.type : 'post';
      this.password = params && params.password ? params.password : '';
      this.permalink_template = params && params.permalink_template ? params.permalink_template : '';
      this.title = params && params.title ? params.title : {
        rendered: ''
      };
      this.content = params && params.content ? params.content : '';
      this.author = params && params.author ? params.author : 0;
      this.excerpt = params && params.excerpt ? params.excerpt : '';
      this.featured_media = params && params.featured_media ? params.featured_media : 0;
      this.comment_status = params && params.comment_status ? params.comment_status : '';
      this.ping_status = params && params.ping_status ? params.ping_status : '';
      this.format = params && params.format ? params.format : '';
      this.meta = params && params.meta ? params.meta : '';
      this.sticky = params && params.sticky ? params.sticky : false;
      this.template = params && params.template ? params.template : '';
      this.categories = params && params.categories ? params.categories : [];
      this.tags = params && params.tags ? params.tags : [];
    }
  }

  let posts: Post[] = [];

  onMount(async () => {
    const url = `${POSTS_API_URI}posts/?per_page=3`;
    const response = await fetch(url);
    const data = await response.json();

    for (const datum of data) {
      const post = new Post(datum);
      posts.push(post);
    }

    posts = [...posts];
  });

  const onPostClick = (post: Post): void => {
    location.assign(post.link);
  }
</script>

<style>
  #footer {
    margin-top: 0;
  }

  #footer a,
  #footer a:visited,
  #footer a:active {
    color: var(--link-alt-color);
    text-decoration: none;
    transition: all 300ms linear;
  }

  #footer a:hover {
    color: var(--link-alt-color);
    text-decoration: underline;
  }


  main > footer {
    background-color: var(--tertiary);
    color: var(--tertiary-text);
  }

  main > footer > .footer {
    display: flex;
    align-items: center;
    height: 64px;
    padding: var(--page-padding);
    background-color: var(--dark);
    color: var(--light);
  }

  main > footer > .content {
    height: 384px;
    max-width: calc(1920px - 256px);
    margin: 0 auto;
    padding: 2vw 0;
    padding-right: calc(var(--page-padding-right) / 2);
    padding-left: calc(var(--page-padding-left) / 2);
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  main > footer > .content > div {
    flex: 1;
  }
  
  main > footer > .content > div > .social-list-item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  main > footer > .content > div > .social-list-item:not(:last-of-type) {
    margin-bottom: 8px;
  }

  main > footer > .content > div > .social-list-item > img {
    width: 28px;
    margin-right: 8px;
  }

  main > footer > .content > div > h4 {
    font-size: 24px;
  }

  main > footer > .content > div > ul {
    padding-left: 0;
  }

  main > footer > .content > div > ul > li {
    list-style-type: none;
    margin-bottom: 1vw;
  }

  main > footer > .content > div > ul > li > .media {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    transition: all .2s linear;
    cursor: pointer;
  }

  main > footer > .content > div > ul > li > .media:hover {
    color: var(--accent);
    text-decoration: underline;
  }

  main > footer > .footer img {
    margin-right: 4vw;
    /* height: 48px; */
    width: 128px;
  }
  main > footer > .footer span a {
    padding: 8px 16px;
    color: var(--accent-dark);
    text-decoration: underline;
    transition: all 300ms linear;
  }
  main > footer > .footer span a:hover {
    color: var(--accent);
  }

  main > footer > .footer span a:active {
    text-decoration: none;
  }

  /* Small devices (landscape phones, 767px and up) */
  @media (max-width: 767px) {
    main > footer > .content,
    main > footer > .footer {
      flex-direction: column;
      height: auto;
    }

    main > footer > .content > #more-site-links {
      display: none;
    }

    main > footer > .footer {
      align-items: flex-start;
      padding: 4vw;
    }

    main > footer > .footer > span:not(:last-child) {
      margin-bottom: 8px;
    }

    main > footer > .footer > img {
      margin-bottom: 24px;
    }

    main > footer > .footer > span:last-child {
      flex-direction: column;
    }

    main > footer > .footer > span:last-child > a {
      padding-left: 0;
    }
  }

  /* Medium devices (tablets, 768px and up) */
  @media (min-width: 768px) {

  }

  /* Large devices (desktops, 992px and up) */
  @media (min-width: 992px) {

  }

  /* Extra large devices (large desktops, 1200px and up) */
  @media (min-width: 1200px) {
  }

  /* Super large devices (large desktops, 1400px and up) */
  @media (min-width: 1400px) {
  }
</style>

<main id="footer">
  <footer>
    <div class="content">
      <div>
        <h4>Contact Us</h4>
        <p>{@html companyAddress}</p>
        <p>902.220.7233</p>
        <p>hello@curbza.com</p>
      </div>

      <div id="more-site-links">
        <h4>More Site Links</h4>
        <ul>
          <li><a href="//blog.curbza.com">Blog</a></li>
          <li><a href="/case-studies-ppe-marketplace">PPE Marketplace Case Study</a></li>
          <li><a href="//bloc.curbza.com/careers">Careers</a></li>
        </ul>
      </div>

      <div>
        <h4>Blog</h4>
        <ul>
          {#each posts as post}
          <li>
            <div class="media clickable" on:click={() => { onPostClick(post); }}>
              <!-- <img class="media-img" src={post.FeaturedImage} alt={post.Title}> -->
              <div class="media-content">
                <h3>{post.Title}</h3>
                <h4>{new Date(post.date).toDateString()}</h4>
              </div>
            </div>
          </li>
          {/each}
        </ul>
      </div>

      <div>
        <h4>Follow Us</h4>
        {#each socialLinks as item}
        <div class="social-list-item">
          <img src={item.iconPath} alt={item.name}>
          <a href={item.link} target="_blank">{item.name}</a>
        </div>
        {/each}
      </div>

      <div>
        <!-- <h4>Certifications</h4> -->
      </div>
    </div>
    <div class="footer">
      <img src="/assets/images/logos/logo-light.png" alt="Cubrza white logo">
      <span>{@html copyright}</span>
      <span class="spacer"></span>
      <span>
        <a href="/privacy-policy">Privacy Policy</a>
        <a href="/cookie-policy">Cookie Policy</a>
        <a href="/terms-of-service">Terms of Service</a>
      </span>
    </div>
  </footer>
</main>